import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Shield,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  User,
  Clock,
  FileText,
} from "lucide-react";
import { auditLogsApi, usersApi, PaginatedResponse } from "../../services/api";
import SearchableSelect from "../ui/SearchableSelect";

interface AuditEntry {
  _id: string;
  userId: { _id: string; name: string; email: string } | null;
  actorUserId: { _id: string; name: string; email: string } | null;
  action: string;
  entity: string | null;
  entityId: string | null;
  description: string | null;
  changes: Record<string, { old: any; new: any }> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

interface TeamUser {
  _id: string;
  name: string;
  email: string;
}

const ACTION_COLORS: Record<string, string> = {
  create: "bg-emerald-100 text-emerald-700",
  update: "bg-blue-100 text-blue-700",
  delete: "bg-red-100 text-red-700",
  login: "bg-purple-100 text-purple-700",
  export: "bg-amber-100 text-amber-700",
};

const entityOptions = [
  { value: "", label: "All Entities" },
  { value: "client", label: "Client" },
  { value: "matter", label: "Matter" },
  { value: "invoice", label: "Invoice" },
  { value: "payment", label: "Payment" },
  { value: "document", label: "Document" },
  { value: "trust_receipt", label: "Trust Receipt" },
  { value: "trust_disbursement", label: "Trust Disbursement" },
  { value: "task", label: "Task" },
  { value: "user", label: "User" },
  { value: "calendar_event", label: "Calendar Event" },
  { value: "intake_form", label: "Intake Form" },
  { value: "intake_submission", label: "Intake Submission" },
];

const actionOptions = [
  { value: "", label: "All Actions" },
  { value: "create", label: "Create" },
  { value: "update", label: "Update" },
  { value: "delete", label: "Delete" },
  { value: "login", label: "Login" },
  { value: "export", label: "Export" },
  { value: "approve", label: "Approve" },
  { value: "reject", label: "Reject" },
];

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const AuditLogPage: React.FC = () => {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [teamUsers, setTeamUsers] = useState<TeamUser[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [entityFilter, setEntityFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Expanded rows
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (search) params.search = search;
      if (userFilter) params.userId = userFilter;
      if (actionFilter) params.action = actionFilter;
      if (entityFilter) params.entity = entityFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const res = await auditLogsApi.list(params);
      const data = res.data as PaginatedResponse<AuditEntry>;
      setEntries(data.data);
      setPagination((prev) => ({ ...prev, ...data.pagination }));
    } catch (err) {
      console.error("Failed to fetch audit logs:", err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, userFilter, actionFilter, entityFilter, startDate, endDate]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await usersApi.list({ limit: 100 });
      setTeamUsers(res.data?.data || []);
    } catch {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const getUserDisplay = (entry: AuditEntry) => {
    const user = entry.userId || entry.actorUserId;
    return user ? user.name || user.email : "System";
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Audit Log</h1>
        <p className="text-sm text-slate-500 mt-1">
          Track all actions and changes across your firm ({pagination.total} entries)
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search descriptions..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <div className="w-48">
              <SearchableSelect
                options={[
                  { value: "", label: "All Users" },
                  ...teamUsers.map((u) => ({ value: u._id, label: u.name })),
                ]}
                value={userFilter}
                onChange={(val) => {
                  setUserFilter(val || "");
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                placeholder="All Users"
                icon={User}
                clearable
              />
            </div>

            <div className="w-44">
              <SearchableSelect
                options={actionOptions}
                value={actionFilter}
                onChange={(val) => {
                  setActionFilter(val || "");
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                placeholder="All Actions"
                icon={Filter}
                clearable
              />
            </div>

            <div className="w-52">
              <SearchableSelect
                options={entityOptions}
                value={entityFilter}
                onChange={(val) => {
                  setEntityFilter(val || "");
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                placeholder="All Entities"
                icon={FileText}
                clearable
              />
            </div>

            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="From"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="To"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </div>
        ) : entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-500">
            <Shield className="h-12 w-12 mb-3 text-slate-300" />
            <p className="font-medium text-lg">No audit log entries</p>
            <p className="text-sm mt-1">Actions will be recorded here automatically</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ backgroundColor: "var(--color-primary)" }}>
                    <th className="w-8 px-3"></th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                      User
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                      Action
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                      Entity
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden lg:table-cell">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, i) => {
                    const isExpanded = expandedId === entry._id;
                    const actionColor = ACTION_COLORS[entry.action] || "bg-slate-100 text-slate-600";
                    return (
                      <React.Fragment key={entry._id}>
                        <motion.tr
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.02 }}
                          className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                          onClick={() => setExpandedId(isExpanded ? null : entry._id)}
                        >
                          <td className="px-3 py-3">
                            {entry.changes && (
                              isExpanded ? (
                                <ChevronUp className="h-4 w-4 text-slate-400" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-slate-400" />
                              )
                            )}
                          </td>
                          <td className="px-6 py-3">
                            <div className="flex items-center gap-1.5 text-sm text-slate-600">
                              <Clock className="h-3.5 w-3.5 text-slate-400" />
                              {formatDate(entry.createdAt)}
                            </div>
                          </td>
                          <td className="px-6 py-3">
                            <div className="flex items-center gap-2">
                              <div
                                className="h-7 w-7 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0"
                                style={{ backgroundColor: "var(--color-accent)" }}
                              >
                                {getUserDisplay(entry)
                                  .split(" ")
                                  .slice(0, 2)
                                  .map((w) => w[0]?.toUpperCase())
                                  .join("")}
                              </div>
                              <span className="text-sm font-medium text-slate-700">
                                {getUserDisplay(entry)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-3">
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ${actionColor}`}
                            >
                              {entry.action}
                            </span>
                          </td>
                          <td className="px-6 py-3">
                            <span className="text-sm text-slate-600 capitalize">
                              {entry.entity?.replace(/_/g, " ") || "-"}
                            </span>
                          </td>
                          <td className="px-6 py-3 hidden lg:table-cell">
                            <span className="text-sm text-slate-500 truncate block max-w-xs">
                              {entry.description || "-"}
                            </span>
                          </td>
                        </motion.tr>

                        {/* Expanded Row - Change Details */}
                        {isExpanded && entry.changes && (
                          <tr className="bg-slate-50">
                            <td colSpan={6} className="px-6 py-4">
                              <div className="ml-8">
                                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                                  Changes
                                </h4>
                                <div className="grid gap-2">
                                  {Object.entries(entry.changes).map(([field, change]) => (
                                    <div
                                      key={field}
                                      className="flex items-start gap-4 text-sm bg-white rounded-lg p-3 border border-slate-200"
                                    >
                                      <span className="font-medium text-slate-700 w-32 flex-shrink-0 capitalize">
                                        {field.replace(/_/g, " ")}
                                      </span>
                                      <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded text-xs line-through truncate">
                                          {change.old != null ? String(change.old) : "(empty)"}
                                        </span>
                                        <span className="text-slate-400">&rarr;</span>
                                        <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded text-xs truncate">
                                          {change.new != null ? String(change.new) : "(empty)"}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {/* IP/User Agent */}
                                <div className="mt-3 flex gap-4 text-xs text-slate-400">
                                  {entry.ipAddress && <span>IP: {entry.ipAddress}</span>}
                                  {entry.userAgent && (
                                    <span className="truncate max-w-xs">UA: {entry.userAgent}</span>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                </p>
                <div className="flex gap-2">
                  <button
                    disabled={pagination.page <= 1}
                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                    className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    disabled={pagination.page >= pagination.pages}
                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                    className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuditLogPage;
